import { computed, Ref, useContext, useRoute, useStore } from '@nuxtjs/composition-api';
import useBrandConfig from '@shared/composables/useBrandConfig';
import { ISBAsset } from '@shared/types/storyblok';
import {
  ApiError,
  getStoriesAsArray,
  getStoryBlokVersion,
  useContent,
} from '@unified-commerce/gpc-vue-storefront-storyblok';

import { SEARCH_CATEGORY_TENANT_PREFIX } from '~/constants';

export interface SbCategoryDefinition {
  id: string;
  category: {
    name: string;
    description: string;
    product_category: {
      title: string;
      handle: string;
    };
    thumb_image: {
      filename: string;
      alt: string;
    };
    seo_title: string;
    seo_description: string;
    url: {
      url: string;
    };
    categoryName?: string;
    blurb?: string;
    banner_image?: ISBAsset;
    banner_image_mobile?: ISBAsset;
  };
}

export interface UseSbCategoriesProps {
  id: string;
}

export interface UseSbCategoriesReturnProps {
  search: () => Promise<void>;
  error: Ref<ApiError | null>;
  sbCategories: Ref<SbCategoryDefinition[]>;
  firstLevelCategories: Ref<SbCategoryDefinition[]>;
  sbCategoriesLoading: Ref<boolean>;
  getSbCategoryName: (category: SbCategoryDefinition | null) => string;
  getSbCategoryDescription: (category: SbCategoryDefinition | null) => string;
  getSbCategoryThumbImage: (category: SbCategoryDefinition | null) => string;
  getSbCategoryThumbAlt: (category: SbCategoryDefinition | null) => string;
  getSbCategoryById: (categoryId: string) => SbCategoryDefinition | null;
  getSbCategorySeoTitle: (category: SbCategoryDefinition | null) => string;
  getSbCategorySeoDescription: (category: SbCategoryDefinition | null) => string;
  getSbCategoryNameFromText: (link: string) => string | undefined;
  getFirstLevelCategoryLink: (category: SbCategoryDefinition | null) => string;
}

// eslint-disable-next-line max-lines-per-function
const useSbCategories = (
  { id }: UseSbCategoriesProps = { id: 'categories-sb' },
): UseSbCategoriesReturnProps => {
  const { $config } = useContext();
  const route = useRoute();
  const store = useStore() as any;
  const { composableConfig } = useBrandConfig();
  const isStoreBackedMode = !!composableConfig?.useBrandLink?.vendorLinks;

  const {
    search: searchSbCategories,
    content: readOnlySbCategories,
    error: readOnlySbCategoriesError,
    loading: readOnlySbCategoriesLoading,
  } = useContent(id);

  const sbCategoriesError = computed(() => readOnlySbCategoriesError.value.search as ApiError);

  const storeBackedSbCategories = computed(() =>
    Object.values(store?.state?.sbCategoryDefinitions ?? {}),
  );

  const mapStoryContentToCategory = (storyContent: any): SbCategoryDefinition | undefined => {
    const shopifyCategory = storyContent?.product_category?.[0]?.shopify_category?.items?.[0];

    if (!shopifyCategory?.id) {
      return;
    }

    return {
      id: shopifyCategory.id,
      category: {
        ...storyContent,
        product_category: {
          title: shopifyCategory.name,
          handle: shopifyCategory.handle,
        },
      },
    };
  };

  const search = async () => {
    if (storeBackedSbCategories.value.length || isStoreBackedMode) {
      return;
    }

    await searchSbCategories({
      url: '/content/categories',
      version: getStoryBlokVersion($config.ENV, route.value),
      disablePagination: true,
      custom: {
        filter_query: {
          component: {
            in: 'Category',
          },
        },
        sort_by: 'name:asc',
      },
    });
  };

  const sbCategories = computed(() => {
    const storeCategories = storeBackedSbCategories.value
      .map((category) => mapStoryContentToCategory(category))
      .filter((category): category is SbCategoryDefinition => category !== undefined);

    if (storeCategories.length) {
      return storeCategories;
    }

    return getStoriesAsArray(readOnlySbCategories.value)
      .map((sbCategory) => mapStoryContentToCategory(sbCategory?.content))
      .filter((category): category is SbCategoryDefinition => category !== undefined);
  });

  const firstLevelCategories = computed(() => {
    return sbCategories?.value?.filter((sbCategory) => {
      return sbCategory?.category?.product_category?.title?.split('>').length === 2;
    });
  });

  const getFirstLevelCategoryLink = (sbCategory: SbCategoryDefinition | null) => {
    const categoryHandle = sbCategory?.category?.product_category.handle.replace(
      `${SEARCH_CATEGORY_TENANT_PREFIX}-`,
      '',
    );

    return `parts/${categoryHandle}`;
  };

  const getSbCategoryById = (categoryId: string) => {
    return sbCategories?.value?.find((sbCategory) => sbCategory.id === categoryId) || null;
  };

  const getSbCategoryName = (sbCategory: SbCategoryDefinition | null) => {
    return sbCategory?.category?.name || 'Placeholder Category';
  };

  const getSbCategoryDescription = (sbCategory: SbCategoryDefinition | null) => {
    return sbCategory?.category?.description || '';
  };

  const getSbCategoryThumbImage = (sbCategory: SbCategoryDefinition | null) => {
    return sbCategory?.category?.thumb_image?.filename || '';
  };

  const getSbCategoryThumbAlt = (sbCategory: SbCategoryDefinition | null) => {
    return sbCategory?.category?.thumb_image?.alt || 'Placeholder';
  };

  const getSbCategorySeoTitle = (sbCategory: SbCategoryDefinition | null) => {
    return sbCategory?.category?.seo_title || '';
  };

  const getSbCategorySeoDescription = (sbCategory: SbCategoryDefinition | null) => {
    return sbCategory?.category?.seo_description || '';
  };

  const getSbCategoryNameFromText = (text: string) => {
    return sbCategories.value.find((sbCategory) => sbCategory.category.name === text)?.category
      ?.product_category?.title;
  };

  return {
    search,
    error: sbCategoriesError,
    sbCategories,
    firstLevelCategories,
    sbCategoriesLoading: computed(() =>
      storeBackedSbCategories.value.length || isStoreBackedMode
        ? false
        : readOnlySbCategoriesLoading.value,
    ),
    getSbCategoryName,
    getSbCategoryDescription,
    getSbCategoryThumbImage,
    getSbCategoryThumbAlt,
    getSbCategoryById,
    getSbCategorySeoTitle,
    getSbCategorySeoDescription,
    getSbCategoryNameFromText,
    getFirstLevelCategoryLink,
  };
};

export default useSbCategories;
